import {
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import {
  type ResolvedDateTimeFormat,
  toDate,
  useDateTimeFormat,
} from "@/shared/datetime";
import { useInfiniteSuspenseDatasetFilesQuery } from "@/shared/domain/datasets/hooks";
import { FileRecord } from "@/shared/domain/files";
import { formatByteSize } from "@/shared/text";

import { ActiveSource } from "../useFilePicker";

import styles from "./FileList.module.css";

interface FileListProps {
  activeSource: ActiveSource;
  searchTerm: string;
  onFileToggle: (file: FileRecord) => void;
  isFileSelected: (fileId: string) => boolean;
  isFileAlreadyAdded: (fileId: string) => boolean;
  includePatterns?: string[];
}

export function FileList(props: FileListProps) {
  const {
    activeSource,
    searchTerm,
    includePatterns,
    isFileSelected,
    isFileAlreadyAdded,
    onFileToggle,
  } = props;

  // Fetch files for active source with pagination
  const activeDatasetId =
    activeSource?.type === "dataset" ? activeSource.id : undefined;

  // When both a search term and includePatterns are provided, weave the search
  // term into each pattern so the results satisfy both constraints (patterns
  // are OR-ed by the backend).  e.g. ["**/*.json", "**/*.yaml"] + "config"
  // becomes ["**/*config*.json", "**/*config*.yaml"].
  const mergedPatterns = React.useMemo(() => {
    if (searchTerm && includePatterns) {
      return includePatterns.map((p) => {
        const dotIdx = p.lastIndexOf(".");
        if (dotIdx === -1) {
          return p;
        }
        const ext = p.slice(dotIdx);
        return `**/*${searchTerm}*${ext}`;
      });
    }
    if (searchTerm) {
      return [`**/*${searchTerm}*`];
    }
    return includePatterns;
  }, [searchTerm, includePatterns]);

  const filesQuery = useInfiniteSuspenseDatasetFilesQuery({
    datasetId: activeDatasetId,
    includePatterns: mergedPatterns,
    limit: 50,
    sortBy: "relative_path",
    sortDirection: "ASC",
  });

  const dateTimeFormat = useDateTimeFormat();
  // Transform file records into a selectable file list item
  const selectableFiles = React.useMemo(() => {
    return filesQuery.data.map((file) => {
      const alreadyAdded = isFileAlreadyAdded(file.file_id);
      const selected = isFileSelected(file.file_id);

      return {
        id: file.file_id,

        /** Whether the file has been selected by the user */
        isSelected: selected && !alreadyAdded,

        /** The file cannot be selected by the user */
        isDisabled: alreadyAdded,

        /** Whether the file's checkbox should be checked */
        isChecked: alreadyAdded || selected,

        /** Primary file text */
        primaryText: file.name,

        /** Auxiliary file text */
        secondaryText: alreadyAdded
          ? "Already added"
          : `${formatByteSize(file.size)} • ${formatRelativeDate(file.created, dateTimeFormat)}`,

        /** The underlying file record */
        record: file,
      };
    });
  }, [filesQuery.data, isFileAlreadyAdded, isFileSelected, dateTimeFormat]);

  if (selectableFiles.length === 0) {
    const noFilesMessage = () => {
      if (searchTerm) {
        return "No files match your search";
      }

      if (activeSource === null) {
        // Don't display anything if no active source
        return "";
      }

      return `No files in this ${activeSource.type}`;
    };
    return (
      <div className={styles.emptyContainer}>
        <RobotoTypography color="text.secondary" variant="body2">
          {noFilesMessage()}
        </RobotoTypography>
      </div>
    );
  }

  return (
    <div className={styles.listContainer}>
      <List dense disablePadding className={styles.list}>
        {selectableFiles.map((file) => {
          return (
            <ListItem key={file.id} disablePadding>
              <ListItemButton
                className={classNames(styles.listItem, {
                  [styles.disabled]: file.isDisabled,
                })}
                onClick={() => {
                  if (!file.isDisabled) {
                    onFileToggle(file.record);
                  }
                }}
                selected={file.isSelected}
                disabled={file.isDisabled}
              >
                <Checkbox
                  checked={file.isChecked}
                  disabled={file.isDisabled}
                  tabIndex={-1}
                  disableRipple
                  className={styles.checkbox}
                />
                <ListItemText
                  primary={file.primaryText}
                  secondary={file.secondaryText}
                  primaryTypographyProps={{
                    noWrap: true,
                    title: file.record.relative_path,
                    className: styles.title,
                  }}
                  secondaryTypographyProps={{
                    variant: "caption",
                    className: styles.subtitle,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {filesQuery.hasNextPage && (
        <button
          aria-labelledby="load-more-files"
          className={styles.loadingButton}
          onClick={() => void filesQuery.fetchNextPage()}
          disabled={filesQuery.isFetchingNextPage}
        >
          {filesQuery.isFetchingNextPage ? (
            <CircularProgress size={16} />
          ) : (
            <span id="load-more-files">Load more</span>
          )}
        </button>
      )}
    </div>
  );
}

/**
 * Says when a file was created: "Today", "Yesterday" or "N days ago" within a
 * week of now, otherwise its date in the viewer's date format.
 */
function formatRelativeDate(
  isoString: string,
  dateTimeFormat: ResolvedDateTimeFormat,
): string {
  const date = toDate(isoString);
  if (date === null) {
    return "";
  }
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return "Today";
  }
  if (diffDays === 1) {
    return "Yesterday";
  }
  if (diffDays < 7) {
    return `${diffDays} days ago`;
  }

  return dateTimeFormat.format(date, { time: "none" });
}
